/**
 * The staff shell: phone first, thumb first.
 *
 * Navigation is a bottom tab bar because that is where a thumb lives, and it
 * sits above the home indicator via `env(safe-area-inset-bottom)` so the last
 * tab is not half under the gesture bar on an iPhone.
 *
 * Nothing in this shell shows a wage figure. The roster response omits cost for
 * staff by contract, and nothing here reintroduces it: a team member's own pay
 * is on their payslips, and what the business spends is not their screen.
 */
import { useCallback } from 'react';
import { Link, Route, Switch, useLocation } from 'wouter';
import { Bell, CalendarDays, CalendarX, LogOut, Receipt, Timer } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, qk } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { useRealtime } from '../lib/socket.ts';
import { Avatar, PandaMark } from './Bits.tsx';
import { InstallApp } from './InstallApp.tsx';
import MyWeekPage from '../pages/staff/MyWeekPage.tsx';
import ClockPage from '../pages/staff/ClockPage.tsx';
import UnavailabilityPage from '../pages/staff/UnavailabilityPage.tsx';
import MyPayslipsPage from '../pages/staff/MyPayslipsPage.tsx';
import NotificationsPage from '../pages/staff/NotificationsPage.tsx';
import CoverPage from '../pages/staff/CoverPage.tsx';

/** The header greets by the time of day, because "Ava" alone is a label and a
 *  greeting is a person being spoken to. */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const TABS = [
  { href: '/', label: 'My week', icon: CalendarDays, match: (l: string) => l === '/' },
  { href: '/clock', label: 'Clock', icon: Timer, match: (l: string) => l.startsWith('/clock') },
  { href: '/unavailable', label: 'Days off', icon: CalendarX, match: (l: string) => l.startsWith('/unavailable') },
  { href: '/payslips', label: 'Pay', icon: Receipt, match: (l: string) => l.startsWith('/payslips') },
  { href: '/alerts', label: 'Alerts', icon: Bell, match: (l: string) => l.startsWith('/alerts') },
] as const;

export default function StaffShell() {
  const { tenant, me, signOut } = useSession();
  const [location] = useLocation();
  const slug = tenant?.tenantSlug ?? '';
  const onNotify = useCallback(() => undefined, []);
  useRealtime(slug || null, onNotify);

  // The unread count is a badge, so a stale value is worse than a slow one:
  // this refetches on a short interval as well as on socket events.
  const unread = useQuery({
    queryKey: qk.notifications(slug),
    queryFn: () => api.notifications(slug),
    enabled: slug !== '',
    refetchInterval: 60_000,
  });
  const unreadCount = (unread.data?.items ?? []).filter((nn) => nn.readAt === null).length;

  return (
    <div className="flex min-h-screen flex-col">
      <header
        className="sticky top-0 z-20 flex items-center gap-2.5 px-4"
        style={{
          height: 'var(--sp-topbar)',
          background: 'color-mix(in srgb, var(--sp-bg) 88%, transparent)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--sp-border)',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <PandaMark size={28} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-extrabold leading-tight text-ink">
            {greeting()}, {me?.user.name.split(' ')[0]}
          </span>
          <span className="block truncate text-[11.5px] leading-tight text-faint">{tenant?.tenantName}</span>
        </span>
        <Avatar name={me?.user.name ?? '?'} size={30} />
        <button
          type="button"
          className="btn btn-icon btn-sm btn-ghost"
          onClick={() => void signOut()}
          title="Sign out|Ends this session on this device."
          aria-label="Sign out"
        >
          <LogOut size={15} aria-hidden="true" />
        </button>
      </header>

      {/* Offered once, at the top of the staff app where it is relevant, and
          never again once installed or dismissed. */}
      <InstallApp />

      <main key={location.split('/').slice(0, 2).join('/')} className="anim-page-in min-w-0 flex-1 pb-24">
        <Switch>
          <Route path="/" component={MyWeekPage} />
          <Route path="/clock" component={ClockPage} />
          <Route path="/unavailable" component={UnavailabilityPage} />
          <Route path="/payslips" component={MyPayslipsPage} />
          <Route path="/payslips/:id" component={MyPayslipsPage} />
          <Route path="/cover" component={CoverPage} />
          <Route path="/alerts" component={NotificationsPage} />
          <Route>
            <div className="px-6 py-16 text-center">
              <h1 className="text-[17px] font-bold text-ink">Nothing here</h1>
              <Link href="/" className="btn btn-primary mt-4">
                Back to my week
              </Link>
            </div>
          </Route>
        </Switch>
      </main>

      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex"
        style={{
          background: 'var(--sp-surface)',
          borderTop: '1px solid var(--sp-border)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          boxShadow: '0 -4px 16px rgb(20 16 12 / 0.06)',
        }}
        aria-label="Main"
      >
        {TABS.map((tab) => {
          const active = tab.match(location);
          const Icon = tab.icon;
          return (
            <Link
              key={tab.href}
              href={tab.href}
              className="tab-link relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold"
              style={{ color: active ? 'var(--sp-accent-strong)' : 'var(--sp-text-faint)', transition: 'color 200ms var(--sp-ease-out)' }}
              aria-current={active ? 'page' : undefined}
            >
              <span className="tab-pill relative">
                <Icon size={20} aria-hidden="true" strokeWidth={active ? 2.4 : 2} />
                {tab.href === '/alerts' && unreadCount > 0 ? (
                  <span
                    className="tabular absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center px-1 text-[10px] font-extrabold"
                    style={{
                      borderRadius: 'var(--sp-radius-pill)',
                      background: 'var(--sp-warn)',
                      color: 'var(--sp-on-solid)',
                    }}
                    aria-label={`${unreadCount} unread`}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                ) : null}
              </span>
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
